import Analytics from 'analytics';
//@ts-ignore
import googleAnalytics from '@analytics/google-analytics'; // https://github.com/DavidWells/analytics/issues/107
import ReactPixel from 'react-facebook-pixel';
import TiktokPixel from './TiktokPixel';
import { IN_DEVELOPMENT } from '../constants';
import EVENTS from '../constants/events';

import debug from './debug';

import { centsToDollars } from '../libs/polygon-ordering/src/utils/misc';
import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent as firebaseEvent } from 'firebase/analytics';
import { v4 as uuidv4 } from 'uuid';

function getCurrency() {
  const currency = 'AUD';

  return currency;
}

function getContentIDs(items: any) {
  const content_ids = [];

  for (let i = 0; i < items.length; i++) {
    content_ids.push(`${items[i].id}`);
  }

  return content_ids?.length > 1 ? `[${content_ids.join(',')}]` : content_ids[0];
}

function getContent(items: any, provider: string) {
  const content = [];

  if (provider === 'facebook') {
    for (let i = 0; i < items.length; i++) {
      content.push(`{id:${items[i].id}, quantity:${items[i].quantity}}`);
    }

    return `[${content.join(',')}]`;
  }
  if (provider === 'google') {
    for (let i = 0; i < items.length; i++) {
      content.push({
        item_id: items[i].id,
        item_name: items[i].name,
        price: centsToDollars(items[i].totalPrice),
        currency: getCurrency(),
      });
    }
    return content;
  }

  if (provider === 'tiktok') {
    for (let i = 0; i < items.length; i++) {
      content.push({
        content_id: items[i].id,
        content_type: 'product',
        quantity: items[i].quantity,
        content_name: items[i].name,
        price: centsToDollars(items[i].totalPrice),
      });
    }
    return content;
  }
}

function getInitialised() {
  const google = Boolean(window._GAnalytics);
  const facebook = Boolean(window._FBAnalytics);
  const tiktok = Boolean(window.ttq);
  const firebase = Boolean(window.firebase);

  const noAnalytics = !google && !facebook && !firebase && !tiktok;

  return {
    google,
    facebook,
    noAnalytics,
    firebase,
    tiktok,
  };
}

export function initAnalytics({
  googleAnalyticsKey,
  facebookAnalyticsKey,
  member,
  firebaseConfig,
  tiktokAnalyticsKey,
}: {
  googleAnalyticsKey: string;
  facebookAnalyticsKey: string;
  member?: Member;
  firebaseConfig: FirebaseConfig;
  tiktokAnalyticsKey: string;
}) {
  try {
    const { google, facebook, tiktok, firebase } = getInitialised();

    let tiktokAdvancedMatching: AdvancedMatching = {};
    let advancedMatching: AdvancedMatching = {};

    if (member) {
      advancedMatching.em = member.email;
      advancedMatching.fn = member.givenName;
      advancedMatching.ln = member.surname;
      tiktokAdvancedMatching.email = member.email;
      if (member.mobile) {
        tiktokAdvancedMatching.phone = member.mobile;
      }
    }

    if (firebase) {
      const app = initializeApp(firebaseConfig);
      getAnalytics(app);
    }

    if (tiktokAnalyticsKey && !tiktok) {
      TiktokPixel.init(tiktokAnalyticsKey, tiktokAdvancedMatching, {
        debug: true,
      });
      debug('initialised tiktok analytics', { tiktokAnalyticsKey });
    }

    if (googleAnalyticsKey && !google) {
      window._GAnalytics = Analytics({
        app: 'polygon-web-ordering',
        version: process.env.REACT_APP_VERSION,
        plugins: [
          googleAnalytics({
            trackingId: googleAnalyticsKey,
          }),
        ],
      });

      debug('initialised google analytics', { googleAnalyticsKey });
    }

    if (facebookAnalyticsKey && !facebook) {
      //@ts-ignore
      ReactPixel.init(facebookAnalyticsKey, advancedMatching, {
        autoConfig: false, // https://developers.facebook.com/docs/facebook-pixel/advanced/
        debug: false, // logs
      });

      window._FBAnalytics = true;

      debug('initialised facebook analytics', { facebookAnalyticsKey });
    }
  } catch (e) {
    console.error('initAnalytics failed', { e });
  }
}

export function logScreen(screen: string) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('SCREEN:', screen);
      return;
    }

    if (tiktok) {
      TiktokPixel.pageView();
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'screen_view', {
        firebase_screen: screen,
      });
    }

    if (google) {
      window._GAnalytics.page();
    }

    if (facebook) {
      ReactPixel.pageView();
    }
  } catch (e) {
    console.error('logScreen failed', { e });
  }
}
export function logModal(modal: string | null) {
  try {
    const { noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('MODAL:', modal);
      return;
    }

    logEvent(modal ? EVENTS.OPEN_MODAL : EVENTS.CLOSE_MODAL, { label: modal });
  } catch (e) {
    console.error('logModal failed', { e });
  }
}

type LogUserData = {
  cardsetId: string;
  memberNumber: string;
  verified: boolean;
  type: string;
};

export function logUser(userId?: string | null, data?: LogUserData) {
  const userIdOrGuest = userId || 'GUEST';
  try {
    const { google, facebook, firebase, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || !noAnalytics) {
      const logParts: (string | LogUserData)[] = [userIdOrGuest];

      if (data) {
        logParts.push(data);
      }

      debug('USER:', ...logParts);

      return;
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      analytics.setUserId(userIdOrGuest);
    }

    if (google) {
      window._GAnalytics.identify(userIdOrGuest, data);
    }

    if (facebook) {
      // is there a corresponding function?
    }
  } catch (e) {
    console.error('logUser failed', { e });
  }
}

type LogEvent = {
  action: string;
  category: string;
};
type LogEventParams = { category?: string; label?: string; value?: string | number };
type LogEventData = { label?: string | null; value?: number | string };

export function logEvent(event: LogEvent, data?: LogEventData) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      const logParts: (string | LogEventData)[] = [event.action];

      if (data) {
        logParts.push(data);
      }

      debug('EVENT:', ...logParts);

      return;
    }

    const params: LogEventParams = {};

    params.category = event.category;

    if (data?.label != null) {
      params.label = data.label;
    }

    if (data?.value != null) {
      params.value = data.value;
    }

    if (tiktok) {
      // No custom tracking
    }

    if (firebase) {
      const analytics = getAnalytics();
      firebaseEvent(analytics, event.action, params);
    }

    if (google) {
      window._GAnalytics.track(event.action, params);
    }

    if (facebook) {
      // More info about custom events:
      // https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking#custom-events
      ReactPixel.trackCustom(event.action, params);

      // More info about standard events:
      // https://developers.facebook.com/docs/facebook-pixel/implementation/conversion-tracking#standard-events
      // https://developers.facebook.com/docs/facebook-pixel/reference#standard-events
      // ReactPixel.track(event, data);
    }
  } catch (e) {
    console.error('logEvent failed', { e });
  }
}

export function paymentInfo(items: any, total: number, payment_method: PAYMENT_METHOD) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('PaymentInfo:', { items, total, payment_method });
      return;
    }

    if (tiktok) {
      TiktokPixel.track('AddPaymentInfo', {
        content_category: payment_method,
        contents: getContent(items, 'tiktok'),
        currency: getCurrency(),
        value: centsToDollars(total),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'add_payment_info', {
        currency: getCurrency(),
        value: centsToDollars(total),
        items: getContent(items, 'google'),
        payment_type: payment_method,
      });
    }

    if (facebook) {
      ReactPixel.track('AddPaymentInfo', {
        content_category: payment_method,
        content_ids: getContentIDs(items),
        currency: getCurrency(),
        value: centsToDollars(total),
        content_type: 'product',
      });
    }
  } catch (err) {
    console.log('PaymentInfo logging failed.');
  }
}

export function addToCart(product: Item, moneyPrice: number, quantity: number) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('AddtoCart:', { product, moneyPrice, quantity });
      return;
    }

    if (tiktok) {
      TiktokPixel.track('AddToCart', {
        content_type: 'product',
        quantity,
        content_name: product.name,
        content_id: `${product.id}`,
        currency: getCurrency(),
        value: centsToDollars(moneyPrice),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'add_to_cart', {
        currency: getCurrency(),
        value: centsToDollars(moneyPrice),
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            quantity,
            price: centsToDollars(moneyPrice),
            currency: getCurrency(),
          },
        ],
      });
    }

    if (facebook) {
      ReactPixel.track('AddToCart', {
        content_name: product.name, // name of the page
        content_type: 'product', // always product
        content_ids: [`${product.id}`], // array of numbers or strings
        currency: getCurrency(),
        value: centsToDollars(moneyPrice),
      });
    }
  } catch (err) {
    console.log('AddtoCart logging failed.');
  }
}

export function purchase(items: any, total: number) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('Purchase:', { items, total });
      return;
    }

    if (tiktok) {
      TiktokPixel.track('CompletePayment', {
        contents: getContent(items, 'tiktok'),
        value: centsToDollars(total),
        currency: getCurrency(),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'purchase', {
        currency: getCurrency(),
        value: centsToDollars(total),
        items: getContent(items, 'google'),
        transaction_id: uuidv4(), //  helps you avoid getting duplicate events for a purchase
      });
    }

    if (facebook) {
      ReactPixel.track('Purchase', {
        content_type: 'product', // always product
        content_ids: getContentIDs(items), // array of numbers or strings
        currency: getCurrency(),
        value: centsToDollars(total),
      });
    }
  } catch (err) {
    console.log('Purchase logging failed.');
  }
}

export function registerUser(
  content_name: string,
  status: boolean,
  value?: OrderTotals,
  items?: any,
) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('REGISTER USER:', content_name, status, value, items);
      return;
    }

    if (tiktok) {
      TiktokPixel.track('CompleteRegistration', {
        contents: getContent(items, 'tiktok'),
        content_name,
        value: centsToDollars(value?.discountedMoneyPrice) || 0,
        currency: getCurrency(),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      firebaseEvent(analytics, 'sign_up', { method: content_name });
    }

    if (facebook) {
      ReactPixel.track('CompleteRegistration', {
        content_name,
        status,
        value: centsToDollars(value?.discountedMoneyPrice) || 0,
        currency: getCurrency(),
      });
    }
  } catch (err) {
    console.log('Registering user failed', err);
  }
}

export function initiateCheckout(items: any, total: number) {
  try {
    const { google, facebook, firebase, tiktok, noAnalytics } = getInitialised();

    if (IN_DEVELOPMENT || noAnalytics) {
      debug('INITIATE CHECKOUT:', items, total);
      return;
    }

    if (tiktok) {
      TiktokPixel.track('InitiateCheckout', {
        contents: getContent(items, 'tiktok'),
        value: centsToDollars(total),
        currency: getCurrency(),
      });
    }

    if (firebase) {
      const analytics = getAnalytics();
      //@ts-ignore
      firebaseEvent(analytics, 'begin_checkout', {
        currency: getCurrency(),
        value: centsToDollars(total),
        items: getContent(items, 'google'),
      });
    }

    if (facebook) {
      ReactPixel.track('InitiateCheckout', {
        content_ids: getContentIDs(items),
        currency: getCurrency(),
        num_items: parseInt(items.length),
        value: centsToDollars(total),
        content_type: 'product',
      });
    }
  } catch (err) {
    console.log('InitiateCheckout Failed', err);
  }
}
